import React, { useState, useMemo, useCallback, useLayoutEffect, useRef } from 'react'
import clsx from 'clsx'
import { PaginationRes, AgentMemo, AgentMemoDepartment, PermissionType } from '@golden/gdk-admin'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import CoreTable from '../../default/present/CoreTable'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import ScrollablePaper from '../../default/present/ScrollablePaper'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import useGDK from '../../../providers/admin/gdk/useGDK'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { createDefaultPaginationData, createTableData } from '../../../utils/default/TableHelper'
import { createSearchToRequest } from './PlayerAgentsMemoForm'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { useRequestFromSearch, useGetDataByPayload, usePaginationClickAndChangeUrl, useDialogHandleClick, useGDKFuncHandleClick } from '../../../utils/default/ComplexFlowHook'
import { EditorState, defaultEditorState, parseContent, isEmpty } from '@golden/tiptap-react'
import { useAsyncEffect } from '../../../utils/default/useAsyncEffect'
import allRoutes from '../route/route'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import { TextEditor } from '../../default/form/textEditor/TextEditor'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import { useChecker } from '../../../utils/admin/AdminRouteHook'

interface RowType {
  id: number
  account: string
  department: 'RC' | 'CS'
  memo: React.ReactElement
  operation: React.ReactElement
}

const departmentLabel: Record<AgentMemo['department'], RowType['department']> = {
  rc: 'RC',
  cs: 'CS'
}

const useStyles = makeStyles((theme) => ({
  actions: {
    paddingBottom: theme.spacing(4)
  },
  memo: {
    maxWidth: '400px'
  }
}))

const PlayerAgentMemoDialog: React.FC<{ open: boolean, handleClose: () => void, account: string, department: AgentMemoDepartment, id: number, reload: () => void, disabled: boolean }> = ({ open, handleClose, disabled, id, account, department, reload }) => {
  const gdk = useGDK()
  const { t } = useT()
  const commonClasses = useCommonStyles()
  const classes = useStyles()
  const globalDialog = useGlobalDialog()
  const [memo, setMemo] = useState<EditorState | null>(null)
  const requestId = useRef(0)
  useLayoutEffect(() => {
    requestId.current += 1
    setMemo(null)
  }, [id, open])
  const handleMemoChange = useCallback((value: EditorState) => {
    setMemo(value)
  }, [])
  const payload = useMemo(() => ({ id, department }), [id, department])
  useGetDataByPayload({
    payload,
    gdkFunc: ({ id, department }) => gdk.player.getAgentMemo({ department }, { id }),
    gdkFuncDependencies: [gdk],
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onSuccess: async (res) => {
      const currentRequestId = requestId.current
      const content = await parseContent(res.memo)
      if (currentRequestId !== requestId.current || !open) return
      setMemo({
        ...defaultEditorState,
        content
      })
    },
    onSuccessDependencies: [id, open],
    canLoadData: open
  })

  const updatePaylod = useMemo(() => ({
    department,
    memo: JSON.stringify(memo?.content ?? '')
  }), [department, memo])

  const { handleClick, loading } = useGDKFuncHandleClick({
    payload: updatePaylod,
    gdkFunc: (payload) => {
      return gdk.player.updateAgentMemo(payload, { id })
    },
    gdkFuncDependencies: [gdk],
    onSuccess: () => {
      handleClose()
      reload()
      globalDialog.setConfig(createGlobalDialogConfig({
        message: t('dialog.saveSuccess'),
        showIcon: false,
        showCancel: false
      }))
      globalDialog.setOpen({
        id: `updateAgentMemoSuccess-${id}`,
        value: true,
        isOK: false
      })
    },
    onError: (error) => {
      globalDialog.setConfig(createGlobalDialogConfig({
        variant: 'error',
        message: error.message,
        showIcon: false,
        showCancel: false
      }))
      globalDialog.setOpen({
        id: `updateAgentMemoFailed-${id}`,
        value: true,
        isOK: false
      })
    }
  })

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <Box padding={2} className={commonClasses.dialogPinkHeader}>
        {disabled ? t('common.lookMemo') : t('common.editMemo')}
      </Box>
      <DialogContent>
        <Box>
          <Typography variant="h3">
            { account }
          </Typography>
        </Box>
        <Box>
          {memo !== null && <TextEditor
            value={memo}
            disabled={disabled}
            controls={disabled ? [] : ['bold', 'ol', 'ul', 'textColor', 'clearFormat']}
            onChange={handleMemoChange}
          />}
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Grid container direction="row" spacing={2} justifyContent="center">
          {
            disabled
              ? (
              <Grid item>
                <Button
                  className={commonClasses.greyButton}
                  onClick={handleClose}
                >
                  {t('common.close')}
                </Button>
              </Grid>
                )
              : (
                <>
                  <Grid item>
                    <Button
                      className={commonClasses.greyButton}
                      onClick={handleClose}
                    >
                      {t('common.cancel')}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      className={commonClasses.purpleGradualButton}
                      disabled={loading || !memo || isEmpty(memo.content)}
                      onClick={handleClick}
                    >
                      {t('common.save')}
                    </Button>
                  </Grid>
                </>
                )
          }
        </Grid>
      </DialogActions>
    </Dialog>
  )
}
const editPermissionByDepartment: Record<AgentMemoDepartment, PermissionType> = {
  rc: PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_RC_EDIT,
  cs: PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_CS_EDIT
}

const readPermissionByDepartment: Record<AgentMemoDepartment, PermissionType> = {
  rc: PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_RC_READ,
  cs: PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_CS_READ
}

const PlayerAgentMemoDeleteButton: React.FC<{ account: string, department: AgentMemo['department'], id: number, reload: () => void }> = ({ account, department, id, reload }) => {
  const gdk = useGDK()
  const { t } = useT()
  const globalDialog = useGlobalDialog()
  const commonClasses = useCommonStyles()
  const payload = useMemo(() => ({
    id,
    department
  }), [id, department])
  const writable = useChecker([editPermissionByDepartment[department]])

  const handleDelete = useDialogHandleClick({
    dialogId: `deleteAgentMemo${id}`,
    globalDialog,
    payload,
    gdkFunc: (payload) => {
      return gdk.player.deleteAgentMemo({ department: payload.department }, { id: payload.id })
    },
    gdkFuncDependencies: [gdk],
    changeDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      message: t('dialog.confirmDeleteAgentMemo', { account })
    }),
    successDialogConfig: createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.deleteFinish')
    }),
    getFailDialogConfig: (error) => {
      return createGlobalDialogConfig({
        variant: 'error',
        showIcon: true,
        showCancel: false,
        message: error
      })
    },
    afterSuccessDialog: () => {
      reload()
    }
  })

  return (<Button className={commonClasses.blueGradualOutlineButton} disabled={!writable} onClick={handleDelete}>{t('common.delete')}</Button>)
}

const PlayerAgentMemoLookButton: React.FC<{ account: string, department: AgentMemoDepartment, id: number, reload: () => void }> = ({ account, department, id, reload }) => {
  const { t } = useT()
  const commonClasses = useCommonStyles()
  const [open, setOpen] = useState(false)
  const readable = useChecker([readPermissionByDepartment[department]])

  return (
    <>
      <PlayerAgentMemoDialog open={open} id={id} account={account} department={department} disabled reload={reload} handleClose={useCallback(() => { setOpen(false) }, [])}/>
      <Button className={commonClasses.blueGradualOutlineButton} disabled={!readable} onClick={useCallback(() => { setOpen(true) }, [])}>{t('common.look')}</Button>
    </>
  )
}
const PlayerAgentMemoUpdateButton: React.FC<{ account: string, department: AgentMemo['department'], id: number, reload: () => void }> = ({ account, department, id, reload }) => {
  const { t } = useT()
  const commonClasses = useCommonStyles()
  const [open, setOpen] = useState(false)
  const writable = useChecker([editPermissionByDepartment[department]])

  return (
    <>
      <PlayerAgentMemoDialog open={open} id={id} account={account} department={department} disabled={false} reload={reload} handleClose={useCallback(() => { setOpen(false) }, [])} />
      <Button className={commonClasses.blueGradualOutlineButton} disabled={!writable} onClick={useCallback(() => { setOpen(true) }, [])}>{t('common.update')}</Button>
    </>
  )
}
const PlayerAgentsMemoTable: React.FC<{ reload: () => void, reloadFlag: boolean }> = ({ reload, reloadFlag }) => {
  const commonClasses = useCommonStyles()
  const classes = useStyles()
  const { t } = useT()
  const gdk = useGDK()
  const canReadRc = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_RC_READ])
  const canReadCs = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_CS_READ])
  const canEditRc = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_RC_EDIT])
  const canEditCs = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_CS_EDIT])
  const showDepartment = canReadRc && canReadCs
  const readableByDepartment: Record<AgentMemoDepartment, boolean> = {
    rc: canReadRc,
    cs: canReadCs
  }
  const writableByDepartment: Record<AgentMemoDepartment, boolean> = {
    rc: canEditRc,
    cs: canEditCs
  }
  const [list, setList] = useState<PaginationRes<AgentMemo[]>>(createDefaultPaginationData([]))
  const pageFlow = usePageFlow()
  const searchToRequest = useMemo(() => createSearchToRequest(canReadRc, canReadCs), [canReadRc, canReadCs])
  const request = useRequestFromSearch({ searchToRequest })
  useGetDataByPayload({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    payload: request!,
    gdkFunc: (payload) => gdk.player.getAgentMemoList(payload),
    gdkFuncDependencies: [gdk, reloadFlag],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res: PaginationRes<AgentMemo[]>) => {
      setList(res)
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: request !== undefined
  })
  const tableClasses = useMemo(() => ({
    head: commonClasses.pinkTableHead,
    row: commonClasses.tableRow,
    cellHead: commonClasses.tableCellHead,
    cellBody: commonClasses.pre
  }), [commonClasses])
  const [rows, setRows] = useState<RowType[]>([])
  useAsyncEffect(async (mounted) => {
    if (!mounted) return
    const readableItems = list.data.filter((item) => readableByDepartment[item.department])
    setRows(await Promise.all(readableItems.map((item) => {
      return (
        {
          id: item.id,
          account: item.account,
          department: departmentLabel[item.department],
          memo: (<Box className={classes.memo}>{item.pure_memo}</Box>),
          operation: (
            <>
              <Grid container spacing={2}>
                <Grid item>
                  <PlayerAgentMemoLookButton id={item.id} account={item.account} department={item.department} reload={reload} />
                </Grid>
                { writableByDepartment[item.department] && (
                  <>
                    <Grid item>
                      <PlayerAgentMemoUpdateButton id={item.id} account={item.account} department={item.department} reload={reload} />
                    </Grid>
                    <Grid item>
                      <PlayerAgentMemoDeleteButton id={item.id} account={item.account} department={item.department} reload={reload} />
                    </Grid>
                  </>
                ) }
              </Grid>
            </>
          )
        }
      )
    })))
  }, [list.data, canReadRc, canReadCs, canEditRc, canEditCs])

  const data = useMemo(() => {
    const columns: Array<keyof RowType> = ['account']
    if (showDepartment) columns.push('department')
    columns.push('memo', 'operation')
    return createTableData<RowType>(
      {
        id: {
          label: '',
          value: 'id'
        },
        account: {
          label: t('common.account'),
          value: 'account'
        },
        department: {
          label: t('common.department'),
          value: 'department'
        },
        memo: {
          label: t('common.memoContent'),
          value: 'memo'
        },
        operation: {
          label: t('common.operation'),
          value: 'operation'
        }
      },
      columns,
      rows,
      'id'
    )
  }, [rows, showDepartment, t])

  const handlePagination = usePaginationClickAndChangeUrl({
    request,
    encodePath: allRoutes.playerAgentsMemo.encodePath
  })

  if (request === undefined) return null

  return (
    <ScrollablePaper marginX={5}>
      <Box padding={4}>
        <LoadingAndErrorFrame { ...pageFlow.status }>
          <CoreTable
            classes={tableClasses}
            data={data}
            total={list.total}
            showPagination
            page={request.page}
            onChangePage={handlePagination}
          />
        </LoadingAndErrorFrame>
      </Box>
    </ScrollablePaper>
  )
}

export default React.memo(PlayerAgentsMemoTable)
